import React, { useEffect, useState } from 'react';
import './snackbar.component.scss';
import CloseIcon from '../icons/close-icon';
import { colourTokens } from '../constants/colour-tokens';
import { showHidePointOfInterest } from '../../services/viewer-point-of-interest.service';
import ProgressSpinnerIcon from '../icons/progress-icon-spinner';

export const SNACKBAR_VARIANT = {
    INFORMATION: 'information',
    SUCCESS: 'success',
    WARNING: 'warning'
};

export type SnackbarVariant = (typeof SNACKBAR_VARIANT)[keyof typeof SNACKBAR_VARIANT];
export const defaultDismissTimeout: number = 2500;

export interface SnackbarMessage {
    showDismiss?: boolean;
    showProgress?: boolean;
    message: string;
    heading?: string;
    action?: { label: string; actionClick: () => void };
    variant?: SnackbarVariant;
    dismissTimeout?: number;
    onClose?: () => void;
}

export interface SnackbarComponentProps extends SnackbarMessage {
    id: string;
}

const SnackbarComponent: React.FC<SnackbarComponentProps> = ({
    id,
    showDismiss = true,
    showProgress = false,
    message,
    heading,
    action,
    variant = SNACKBAR_VARIANT.INFORMATION,
    dismissTimeout = defaultDismissTimeout,
    onClose = () => {}
}) => {
    const [dismissedHovered, setDismissedIsHovered] = useState<boolean>(false);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const handleMouseEnter = () => setDismissedIsHovered(true);
    const handleMouseLeave = () => setDismissedIsHovered(false);

    const handleClose = () => {
        setIsVisible(false);
        onClose();
    };

    const parseBold = (message: string): React.ReactNode[] => {
        const messageParts = message.split(/(<b>|<\/b>)/g);

        const result: React.ReactNode[] = [];
        let isBold = false;

        messageParts.forEach((part, i) => {
            if (part === "<b>") {
                isBold = true;
            } else if (part === "</b>") {
                isBold = false;
            } else {
                result.push(
                    isBold ? (
                        <span key={i} className="message__bold">
                            {part}
                        </span>
                    ) : (
                        part
                    )
                );
            }
        });

        return result;
    };

    const parseMessage = (message: string): React.ReactNode => {
        return parseBold(message);
    };

    useEffect(() => {
        // Show viewer message if not currently displayed
        if (!isVisible && id) {
            setIsVisible(true);
        }

        // handle dismissing when timeout is reached
        if (dismissTimeout > 0) {
            const dismissTimer = setTimeout(() => handleClose(), dismissTimeout);
            return () => clearTimeout(dismissTimer);
        }
    }, [id]);

    return (
        <div
            className={`isp__snackbarcomponent ${variant} ${isVisible ? 'visible' : 'hidden'}`}
            key={id}>
            {showProgress && (
                <div className="progress">
                    <ProgressSpinnerIcon />
                </div>
            )}

            <div className="message">
                {heading && <h4>{heading}</h4>}
                <p>{parseMessage(message)}</p>
            </div>

            {action && (
                <div
                    className="action"
                    onClick={action.actionClick}>
                    <div className="action-btn">{action.label}</div>
                </div>
            )}

            {showDismiss && (
                <div
                    className="dismiss"
                    onClick={() => handleClose()}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}>
                    <CloseIcon colour={dismissedHovered ? colourTokens.white : colourTokens.opaqueWhite20} />
                </div>
            )}
        </div>
    );
};

export default SnackbarComponent;
